import { cn } from '@/lib/utils'
import React from 'react'

/**
 * `<button>` puro, e não o `Button` global: aquele chama `useAuth()`, e o
 * error boundary precisa conseguir renderizar justamente quando o erro veio de
 * um provider acima dele.
 */
function Botao({ className, ...props }: React.ComponentProps<'button'>) {
    return (
        <button
            type="button"
            className={cn(
                'inline-flex min-w-16 cursor-pointer items-center justify-center gap-2',
                'rounded-md bg-primary px-6 py-2 shadow-md outline-none border-none',
                'text-xs font-medium tracking-tight text-primary-foreground uppercase',
                'transition-colors',
                'hover:bg-[color-mix(in_oklab,var(--primary)_85%,var(--color-black))]',
                className,
            )}
            {...props}
        />
    )
}

interface IProps {
    children: React.ReactNode
}

export default class ErrorBoundary extends React.Component<IProps> {
    state = {
        error: undefined,
        errorInfo: undefined,
    } as {
        error: Error | undefined
        errorInfo: React.ErrorInfo | undefined
    }

    expandido = false
    copiado = false

    static getDerivedStateFromError(error: Error) {
        return { error }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error(error)
        this.setState({ error, errorInfo })
    }

    render() {
        if (!this.state.error) return this.props.children

        if (
            this.state.error.message &&
            this.state.error.message.includes(
                'Failed to fetch dynamically imported module',
            )
        ) {
            return (
                <div className="flex flex-col gap-4 items-center justify-center h-[calc(100dvh-4rem)] text-center">
                    <h1 className="text-3xl font-bold">
                        Ocorreu um erro ao carregar a aplicação
                    </h1>
                    <h2 className="text-xl max-w-sm">
                        Isso pode ter sido causado por uma atualização
                        automática. Tente recarregar a página.
                    </h2>
                    <Botao
                        className="text-sm"
                        onClick={() => window.location.reload()}
                    >
                        Recarregar
                    </Botao>
                </div>
            )
        }

        return (
            <div className="flex flex-col gap-4 items-center justify-center h-[calc(100dvh-4rem)] container text-center mx-auto">
                <h1 className="text-3xl font-bold">
                    Ocorreu um erro inesperado
                </h1>
                <p
                    className="text-xl overflow-hidden text-ellipsis whitespace-nowrap max-w-8/10"
                    title={this.state.error.toString()}
                >
                    {'message' in this.state.error
                        ? String(this.state.error.message)
                        : (this.state.error as Error).toString()}
                </p>
                <div
                    className={cn(
                        this.expandido
                            ? 'max-h-[calc(50%-4rem)] overflow-y-auto p-4'
                            : 'max-h-0 overflow-hidden p-0',
                        'transition-[max-height,padding] duration-200',
                        'flex-col justify-start items-start',
                        'bg-gray-600 rounded-lg w-fit',
                        'flex max-w-9/10 h-fit',
                    )}
                >
                    <Botao
                        onClick={() => {
                            navigator.clipboard.writeText(
                                `Error: ${String(
                                    this.state.error,
                                )}\n\nStack trace:\n${
                                    this.state.errorInfo?.componentStack
                                }`,
                            )
                            this.copiado = true
                            this.forceUpdate()
                        }}
                        className={cn({ 'bg-success': this.copiado }, 'w-24')}
                    >
                        {this.copiado ? 'Copiado' : 'Copiar'}
                    </Botao>
                    <p
                        className="h-full text-white leading-tight"
                        dangerouslySetInnerHTML={{
                            __html:
                                this.state.errorInfo?.componentStack
                                    ?.replace(/</g, '&lt;')
                                    ?.split('\n')
                                    ?.join('<br />') ?? '',
                        }}
                    />
                </div>
                <div className="grid grid-cols-1 sm:flex gap-4 flex-wrap items-center justify-center">
                    <Botao
                        className="text-sm"
                        onClick={() => {
                            this.expandido = !this.expandido
                            this.forceUpdate()
                        }}
                    >
                        {this.expandido
                            ? 'Ocultar detalhes'
                            : 'Mostrar detalhes'}
                    </Botao>
                    <Botao
                        className="text-sm"
                        onClick={() => window.location.reload()}
                    >
                        Recarregar
                    </Botao>
                    <Botao
                        className="text-sm"
                        onClick={() => (window.location.href = '/')}
                    >
                        Voltar ao início
                    </Botao>
                </div>
            </div>
        )
    }
}
